import { CheckCircle } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  Alert,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { Box } from '@mui/system';
import { useEffect, useState } from 'react';
import { getRevolutionsVessel } from '../../../../api/north-standard/vessels';
import IconStatusButton from '../../../../common-components/icon-status-button/icon-status-button';
import LoadingSpinner from '../../../../common-components/loading-spinner/loading-spinner';
import {
  MyFleetVessel,
  UpdateMyFleetVesselsResponse,
} from '../../../../models/my-fleet.model';
import {
  ProVesselTypeLabels,
  ProVesselTypes,
} from '../../../../models/pro.model';
import { RevolutionsVessel } from '../../../../models/vessels/vessel.model';

interface EditVesselProps {
  vessel: MyFleetVessel;
  onSubmit: (payload: MyFleetVessel) => Promise<UpdateMyFleetVesselsResponse>;
  onCancel: () => void;
  onFinish: () => void;
}

function EditVessel({ vessel, onSubmit, onCancel, onFinish }: EditVesselProps) {
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState(
    vessel.vessel_type || ProVesselTypes.UNKNOWN
  );
  const [name, setName] = useState(vessel.name || '');

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [revolutionsDetails, setRevolutionsDetails] =
    useState<RevolutionsVessel | null>(null);

  const handleTypeChange = (event: any) => {
    setSelectedType(event.target.value);
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const handleReset = () => {
    if (revolutionsDetails) {
      setName(revolutionsDetails.name || '');
      setSelectedType(
        (revolutionsDetails.derived_information
          .vessel_type as ProVesselTypes) || ProVesselTypes.UNKNOWN
      );
    }
  };

  const handleSubmit = () => {
    setSubmitting(true);
    const payload: MyFleetVessel = {
      ...vessel,
      name,
      vessel_type: selectedType,
    };
    onSubmit(payload).then(() => {
      setSubmitting(false);
      setSuccess(true);
      setTimeout(() => onFinish(), 3000);
    });
  };

  const getSubmitButtonContent = () => {
    if (success) return 'Submitted';
    if (submitting) return 'Submitting...';
    return 'Submit';
  };

  const getSubmitButtonIcon = () => {
    if (success) return <CheckCircle />;
    if (submitting) return <CircularProgress size={12} />;
    return null;
  };

  useEffect(() => {
    // Load default vessel data from revs proxy endpoint
    const fetchVesselData = async () => {
      setLoading(true);
      try {
        const response = await getRevolutionsVessel(vessel.imo);
        setRevolutionsDetails(response);
      } catch (error) {
        // Pass - we already check the response to show an appropriate warning if this fails
      } finally {
        setLoading(false);
      }
    };

    fetchVesselData();
  }, [vessel.imo]);

  const vesselTypeChanged =
    selectedType !== (vessel.vessel_type || ProVesselTypes.UNKNOWN);

  return (
    <Box position="relative">
      <Box mb={2} display="flex" alignItems="center">
        <TextField
          label="Name"
          data-testid="vessel-name-field"
          name="name"
          value={name}
          onChange={handleNameChange}
          variant="outlined"
          size="small"
          fullWidth
        />
        <Box
          sx={{
            ml: 1,
            width: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {(() => {
            if (loading) return <LoadingSpinner />;
            if (!revolutionsDetails?.name) {
              return (
                <Tooltip
                  title="Unable to retrieve verified vessel name"
                  data-testid="vessel-name-tooltip"
                  placement="right"
                  arrow
                >
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <WarningAmberOutlinedIcon
                      color="warning"
                      fontSize="small"
                    />
                  </IconButton>
                </Tooltip>
              );
            }
            return (
              <Tooltip
                title={`Verified vessel name: ${revolutionsDetails?.name}`}
                data-testid="vessel-name-tooltip"
                placement="right"
                arrow
              >
                <IconButton size="small" sx={{ ml: 1 }}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            );
          })()}
        </Box>
      </Box>
      <Box mb={2} display="flex" alignItems="center">
        <FormControl fullWidth size="small">
          <InputLabel id="vessel-type-label">Type</InputLabel>
          <Select
            labelId="vessel-type-label"
            data-testid="vessel-type-select"
            label="Type"
            name="type"
            value={selectedType}
            onChange={handleTypeChange}
            sx={{ textAlign: 'left' }}
            MenuProps={{
              PaperProps: {
                sx: { textAlign: 'left' },
              },
            }}
          >
            {Object.entries(ProVesselTypeLabels)
              .filter(([key]) => key !== 'NO_VESSEL_TYPE')
              .map(([key, label]) => (
                <MenuItem key={key} value={key} sx={{ textAlign: 'left' }}>
                  {label}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <Box
          sx={{
            ml: 1,
            width: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {(() => {
            if (loading) return <LoadingSpinner />;
            const vesselType =
              revolutionsDetails?.derived_information?.vessel_type;
            const vesselTypeLabel = vesselType
              ? ProVesselTypeLabels[vesselType as ProVesselTypes] || vesselType
              : '';
            if (!vesselType) {
              return (
                <Tooltip
                  title="Unable to retrieve verified vessel type"
                  data-testid="vessel-type-tooltip"
                  placement="right"
                  arrow
                >
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <WarningAmberOutlinedIcon
                      color="warning"
                      fontSize="small"
                    />
                  </IconButton>
                </Tooltip>
              );
            }
            return (
              <Tooltip
                title={`Verified vessel type: ${vesselTypeLabel}`}
                placement="right"
                data-testid="vessel-type-tooltip"
                arrow
              >
                <IconButton size="small" sx={{ ml: 1 }}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            );
          })()}
        </Box>
      </Box>
      {vesselTypeChanged && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Changing the vessel type may impact the information provided in PRO
          reports.
        </Alert>
      )}
      <Box mt={2}>
        <Stack
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'stretch',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            width: '100%',
          }}
        >
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={!!submitting || !!success}
            sx={{
              flex: 1,
              minWidth: 'max(30%, 120px)',
            }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={handleReset}
            disabled={!revolutionsDetails}
            sx={{
              flex: 1,
              minWidth: 'max(30%, 120px)',
              whiteSpace: 'nowrap',
            }}
          >
            Vessel Defaults
          </Button>
          <IconStatusButton
            tooltipContent="Submit changes"
            onClick={handleSubmit}
            statusIcon={getSubmitButtonIcon()}
            statusText={getSubmitButtonContent()}
            disabled={!!submitting || !!success}
            sx={{
              flex: 1,
              minWidth: 'max(30%, 120px)',
            }}
          />
        </Stack>
      </Box>
    </Box>
  );
}

export default EditVessel;
